import { useQuery } from "@apollo/client";
import { GET_INTERVIEW_SESSION_WITH_PROFILE } from "../gql";
import type {
  ConversationsWithProfile,
  ConversationsWithProfilesConnection,
} from "@casey/db-types";
import { useMemo } from "react";

interface ConversationWithProfileData {
  conversationsWithProfiles: ConversationsWithProfilesConnection;
}

interface ConversationWithProfileVariables {
  conversationId: string;
}

interface UseConversationOptions {
  skip?: boolean;
}

export function useConversationWithProfile(
  conversationId: string,
  options?: UseConversationOptions,
) {
  const { data, loading, error, refetch } = useQuery<
    ConversationWithProfileData,
    ConversationWithProfileVariables
  >(GET_INTERVIEW_SESSION_WITH_PROFILE, {
    variables: { conversationId },
    skip: !conversationId || options?.skip,
    errorPolicy: "all",
  });

  const rawSession = data?.conversationsWithProfiles?.nodes?.[0] || null;

  // Deserialize JSONB fields (context, conversation, questions, sessionProperties)
  const session = useMemo(() => {
    if (!rawSession) return null;

    const deserialized = { ...rawSession };

    // Parse JSONB string fields
    if (typeof rawSession.context === "string") {
      try {
        deserialized.context = JSON.parse(rawSession.context);
      } catch (e) {
        console.error("Failed to parse context:", e);
      }
    }

    if (typeof rawSession.conversation === "string") {
      try {
        deserialized.conversation = JSON.parse(rawSession.conversation);
      } catch (e) {
        console.error("Failed to parse conversation:", e);
      }
    }

    if (typeof rawSession.questions === "string") {
      try {
        deserialized.questions = JSON.parse(rawSession.questions);
      } catch (e) {
        console.error("Failed to parse questions:", e);
      }
    }

    if (typeof rawSession.sessionProperties === "string") {
      try {
        deserialized.sessionProperties = JSON.parse(
          rawSession.sessionProperties,
        );
      } catch (e) {
        console.error("Failed to parse sessionProperties:", e);
      }
    }

    return deserialized;
  }, [rawSession]);

  return {
    session,
    loading,
    error: error?.message || null,
    refetch,
  };
}
